import {
  ShieldCheck,
  Sparkles,
  Network,
  Gauge,
  BadgeCheck,
  Server,
  Plug,
  Cpu,
  Cloud,
  HardDrive,
  Activity,
} from "lucide-react";
import type { ServiceHealth } from "@/types";
import { useExecution } from "@/store/executionStore";
import { HEALTH_META } from "@/components/hive/shared";
import { Sparkline } from "@/components/ui/primitives";

const ICON: Record<string, typeof Server> = {
  ares: ShieldCheck,
  garlic: Sparkles,
  byonc: Network,
  athena: Gauge,
  verify: BadgeCheck,
  wspool: Server,
  github: Plug,
  jira: Plug,
  slack: Plug,
  calendar: Plug,
  providers: Cloud,
  local: Cpu,
  cloud: HardDrive,
};

export function SystemHealthPanel() {
  const health = useExecution((s) => s.systemHealth);
  const healthy = health.filter((h) => h.status === "healthy").length;
  const attention = health.filter((h) => h.status === "needs-attention" || h.status === "offline" || h.status === "degraded").length;

  return (
    <div className="h-full overflow-y-auto no-scrollbar p-4">
      <div className="mb-3 flex items-center gap-2">
        <Activity size={16} className="text-emerald-500" />
        <span className="text-sm font-semibold text-app">System & Agent Health</span>
        <span className="ml-auto flex items-center gap-3 text-[11px]">
          <span className="text-emerald-500">{healthy} healthy</span>
          {attention > 0 && <span className="text-amber-500">{attention} need attention</span>}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-3">
        {health.map((h) => (
          <HealthCard key={h.id} h={h} />
        ))}
      </div>
    </div>
  );
}

function HealthCard({ h }: { h: ServiceHealth }) {
  const m = HEALTH_META[h.status];
  const Icon = ICON[h.id] ?? Server;
  const live = h.status === "healthy" || h.status === "delayed";
  return (
    <div className="relative overflow-hidden rounded-2xl border border-app bg-surface/50 p-3 backdrop-blur-xl">
      <div className="flex items-center gap-2">
        <div className="grid h-8 w-8 place-items-center rounded-lg" style={{ background: `${m.color}1f`, color: m.color }}>
          <Icon size={15} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-semibold text-app">{h.name}</div>
          <div className="text-[10px] text-app-2">{h.category}</div>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold" style={{ color: m.color, borderColor: `${m.color}55`, background: `${m.color}1a` }}>
          <span className="relative flex h-1.5 w-1.5">
            {live && <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" style={{ background: m.color }} />}
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full" style={{ background: m.color }} />
          </span>
          {m.label}
        </span>
      </div>

      <div className="mt-2.5 flex items-end justify-between">
        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px]">
          <Metric label="Latency" value={h.latency} />
          <Metric label="Queue" value={String(h.queueDepth)} />
          <Metric label="Errors" value={`${(h.errorRate * 100).toFixed(1)}%`} warn={h.errorRate > 0.02} />
          <Metric label="Jobs" value={String(h.activeJobs)} />
        </div>
        <div className="opacity-80">
          <Sparkline data={h.spark} color={m.color} width={72} height={28} />
        </div>
      </div>
      {h.costToday > 0 && <div className="mt-1.5 text-[10px] text-app-2">Cost today · <span className="tabular-nums text-app-1">${h.costToday.toFixed(1)}</span></div>}
    </div>
  );
}

function Metric({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div>
      <span className="text-app-2">{label} </span>
      <span className={`font-medium tabular-nums ${warn ? "text-amber-500" : "text-app-1"}`}>{value}</span>
    </div>
  );
}
